import { Icon } from '@eventbrite/eds-icon';
import { ChevronDownChunky } from '@eventbrite/eds-iconography';
import { STYLE_DEFAULT, STYLE_OUTLINED, Tag } from '@eventbrite/eds-tag';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { Button as MarmaladeButton } from '@eventbrite/marmalade';
import React from 'react';
import {
    CATEGORY_FILTER,
    CURRENCY_FILTER,
    DATE_FILTER,
    FILTER_PILLS_DISMISS_MAP,
    FORMAT_FILTER,
    LANGUAGE_FILTER,
    PRICE_FILTER,
} from '../../../../constants/filters';
import './FilterHeaderPills.scss';
import { FilterHeaderPillsToggle } from './FilterHeaderPillsToggle';

interface IGetRenderedFilters {
    date?: string;
    format?: string;
    price?: string;
    category?: string;
    language?: string;
    currency?: string;
    onClose?: Function;
    online?: boolean;
    organizations?: string[];
    hash?: string;
    followedOrganizers?: boolean;
    hashedEventTitle?: string | GenericLazyString;
    shouldRenderInactivePills?: boolean;
    inactivePillsRenderSet: Set<string>;
    onClick?: (key: string) => void;
}

export const INTTERACTIVE_TAG_BACKGROUND_COLOR = 'eds-bg-color--ui-blue';

const ALWAYS_RENDER_FILTER_SET = new Set([
    DATE_FILTER,
    CATEGORY_FILTER,
    FORMAT_FILTER,
    PRICE_FILTER,
    LANGUAGE_FILTER,
    CURRENCY_FILTER,
]);

const getRenderedFilters = ({
    date,
    format,
    price,
    category,
    language,
    currency,
    online,
    organizations,
    hash,
    followedOrganizers,
    onClose,
    hashedEventTitle,
    shouldRenderInactivePills,
    inactivePillsRenderSet,
    onClick,
}: IGetRenderedFilters) => {
    const filterObject: { [key: string]: string | boolean | string[] } = {
        [DATE_FILTER]: date || '',
        [CATEGORY_FILTER]: category || '',
        online: online || '',
        [FORMAT_FILTER]: format || '',
        [PRICE_FILTER]: price || '',
        [LANGUAGE_FILTER]: language || '',
        [CURRENCY_FILTER]: currency || '',
        hash: hash || '',
        followedOrganizers: followedOrganizers || '',
        organizations: organizations || '',
    };

    return Object.keys(filterObject)
        .filter((key) => !!filterObject[key] || inactivePillsRenderSet.has(key))
        .sort((a: string, b: string) => {
            const val1 = filterObject[a] ? 1 : 0;
            const val2 = filterObject[b] ? 1 : 0;

            return val2 - val1;
        })
        .map((key: string) => {
            const isActive = !!filterObject[key];
            let filterDisplay;

            //Special cases
            if (key === 'hash') {
                //return special case filtercontainer (?)
                filterDisplay = gettext(
                    'Events similar to %(hashedEventTitle)s',
                    { hashedEventTitle },
                );
            }

            if (key === 'organizations') {
                filterDisplay = gettext('Events that accept this credit');
            }

            if (!filterDisplay) {
                const { values, label } = FILTER_PILLS_DISMISS_MAP[key];
                const appliedFilter = filterObject[key];
                if (isActive) {
                    if (Array.isArray(appliedFilter)) {
                        const appliedFilters = values.find(
                            (filterVal) => filterVal.value === appliedFilter[0],
                        );

                        if (appliedFilters)
                            filterDisplay = `${appliedFilters?.display}${
                                appliedFilter.length > 1
                                    ? ` + ${appliedFilter.length - 1}`
                                    : ''
                            }`;
                    } else {
                        const filterValue = values.find(
                            (filterVal) =>
                                filterVal.value === filterObject[key],
                        );

                        //If there is no matching value we will defailt to date
                        filterDisplay = filterValue?.display || date;
                    }
                } else {
                    filterDisplay = label;
                }
            }

            return (
                <span
                    className={`filterPill filter-pill-${key}`}
                    key={key}
                    data-testid={`facet-${key}`}
                >
                    <Tag
                        initialIsActive={false}
                        key={key}
                        onClose={onClose?.bind(null, key)}
                        style={
                            shouldRenderInactivePills
                                ? STYLE_OUTLINED
                                : STYLE_DEFAULT
                        }
                        isClosable={isActive}
                        isStatic={!shouldRenderInactivePills}
                        backgroundColor={
                            shouldRenderInactivePills && isActive
                                ? INTTERACTIVE_TAG_BACKGROUND_COLOR
                                : undefined
                        }
                        onClickStatic={() => onClick?.(key)}
                    >
                        <span className="tag-content">
                            <span className="eds-l-pad-right-1">
                                {filterDisplay}
                                {!isActive && shouldRenderInactivePills ? (
                                    <Icon
                                        type={<ChevronDownChunky />}
                                        color="grey-700"
                                        size="small"
                                    />
                                ) : null}
                            </span>
                        </span>
                    </Tag>
                </span>
            );
        });
};

const getAppliedFilterLabel = (numberActiveFilters: number) => {
    if (numberActiveFilters === 0) {
        return null;
    }
    if (numberActiveFilters === 1) {
        return gettext('%(num)s filter applied', { num: numberActiveFilters });
    }
    return gettext('%(num)s filters applied', { num: numberActiveFilters });
};

export interface FilterHeaderProps {
    handleRemoveFilter?: Function;
    selectedCategory?: string;
    selectedPrice?: string;
    selectedDate?: string;
    selectedFormat?: string;
    selectedLanguage?: string;
    selectedCurrency?: string;
    followedOrganizers?: boolean;
    selectedHash?: string;
    selectedOrganizations?: string[];
    isOnline?: boolean;
    handleRemoveOnlineFilter?: Function;
    handleRemoveAllFilters: Function;
    hideActiveFilterLabel?: boolean;
    hashedEventTitle?: string | GenericLazyString;
    openCloseAnimatedDrawer: Function;
    shouldRenderInactivePills?: boolean;
    isBotRequest?: boolean;
    locationSlug?: string;
}

export const FilterHeaderPills = (props: FilterHeaderProps) => {
    const {
        selectedDate,
        selectedCategory,
        selectedPrice,
        selectedFormat,
        selectedLanguage,
        selectedCurrency,
        selectedOrganizations,
        followedOrganizers,
        selectedHash,
        isOnline,
        handleRemoveOnlineFilter,
        handleRemoveFilter,
        handleRemoveAllFilters,
        hideActiveFilterLabel,
        openCloseAnimatedDrawer,
        hashedEventTitle,
        shouldRenderInactivePills,
    } = props;

    const filters = {
        date: selectedDate,
        format: selectedFormat,
        price: selectedPrice,
        category: selectedCategory,
        language: selectedLanguage,
        currency: selectedCurrency,
        online: isOnline,
        followedOrganizers: followedOrganizers,
        hash: selectedHash,
        organizations: selectedOrganizations,
    };

    function onRemoveFilter(filterType: string) {
        const followedOrganizers = 'followedOrganizers';
        const organizations = 'organizations';

        if (filterType === followedOrganizers) {
            handleRemoveFilter?.({ special: true });
        } else if (filterType === organizations) {
            handleRemoveFilter?.({ organizationsOr: true });
        } else if (filterType === 'online') {
            handleRemoveOnlineFilter?.();
        } else {
            handleRemoveFilter?.({ [filterType]: true });
        }
    }

    function handleClearAllFilters() {
        handleRemoveAllFilters(null);

        if (isOnline) {
            handleRemoveOnlineFilter?.();
        }
    }

    const appliedFiltersAsFacets = getRenderedFilters({
        ...filters,
        hashedEventTitle,
        shouldRenderInactivePills,
        inactivePillsRenderSet: shouldRenderInactivePills
            ? ALWAYS_RENDER_FILTER_SET
            : new Set(),
        onClose: onRemoveFilter,
        onClick: (key) => {
            props.openCloseAnimatedDrawer(true, key);
        },
    });

    const numberActiveFilters = Object.values(filters).filter(
        (val) => !!val,
    ).length;
    const hasActiveFilters = numberActiveFilters > 0;
    const numberActiveFiltersLabel = getAppliedFilterLabel(numberActiveFilters);

    if (!shouldRenderInactivePills && !hasActiveFilters) {
        return null;
    }

    return (
        <div
            className={`filter-header ${
                hasActiveFilters
                    ? 'filter-header__active'
                    : 'filter-header__inactive'
            } ${
                shouldRenderInactivePills
                    ? 'filter-header__render-inactive-pills'
                    : ''
            }`}
        >
            {!hideActiveFilterLabel ? (
                <FilterHeaderPillsToggle
                    numberActiveFiltersLabel={numberActiveFiltersLabel}
                    numberActiveFilters={numberActiveFilters}
                    openCloseAnimatedDrawer={openCloseAnimatedDrawer}
                />
            ) : null}
            <div className="filterPills">{[...appliedFiltersAsFacets]}</div>
            {numberActiveFilters > 0 && (
                <div className="clearAll">
                    <MarmaladeButton
                        onClick={() => handleClearAllFilters()}
                        variant="ghost"
                    >
                        {gettext('Clear All')}
                    </MarmaladeButton>
                </div>
            )}
        </div>
    );
};

export default FilterHeaderPills;
